import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHttpClient, useAlert } from '../../../modules/services/Bundle';
import { RequestMethod } from '../../../modules/services/core/_enums';
import { useAuth } from "../../../modules/auth/core/Auth";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { Button, Typography } from '@mui/material'; 

// --- Interfaces e Tipos ---
interface Organization {
    OrganizationId: number;
    OrganizationName: string;
    Idcorporacao: number;
    CorporationName: string;
}
interface CorporationRowData {
    CorporationId: number;
    CorporationName: string;
    Organizations: Organization[];
}


const Corporacoes: React.FC = () => {
    const navigate = useNavigate();
    const httpClient = useHttpClient();
    const alert = useAlert();
    const { auth } = useAuth();
    const user = auth?.User;
    const emailUsuario = user?.Email;

    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [backendUrl] = useState(process.env.REACT_APP_BACKEND_URL);
    const [corporationsTableData, setCorporationsTableData] = useState<CorporationRowData[]>([]);

    const handleEditCorporation = (rowData: CorporationRowData) => {
        const corpData = encodeURIComponent(JSON.stringify(rowData));
        navigate(`/administracao/editar-corporacao?data=${corpData}`);
    };

    const fetchUserOrganizations = async () => {
        if (!emailUsuario) return;

        try {
            const userIdResponse = await fetch(
                `${backendUrl}/api/User/ByEmail/${encodeURIComponent(emailUsuario)}`
            );

            if (!userIdResponse.ok) throw new Error('Erro ao obter ID do usuário');

            const userId = await userIdResponse.json();

            const orgsResponse = await fetch(
                `${backendUrl}/api/UserOrganization/CorpByUser?userId=${userId}`
            );

            if (!orgsResponse.ok) throw new Error('Erro ao obter organizações');

            const orgsData = await orgsResponse.json();

            if (Array.isArray(orgsData)) {
                setOrganizations(orgsData);
            }
        } catch (error) {
            console.error("Erro ao buscar organizações:", error);
            alert.createMessage({
                type: 'error',
                message: 'Erro ao carregar corporações'
            });
        }
    };

    useEffect(() => {
        fetchUserOrganizations();
    }, [emailUsuario]);

    useEffect(() => {
        if (organizations.length > 0) {
            const groupedData = organizations.reduce((acc: { [key: number]: CorporationRowData }, org) => {
                if (!acc[org.Idcorporacao]) {
                    acc[org.Idcorporacao] = {
                        CorporationId: org.Idcorporacao,
                        CorporationName: org.CorporationName,
                        Organizations: []
                    };
                }
                acc[org.Idcorporacao].Organizations.push(org);
                return acc;
            }, {});

            setCorporationsTableData(Object.values(groupedData));
        } else {
            setCorporationsTableData([]); 
        }
    }, [organizations]);



    const tableRef = useRef<HTMLDivElement>(null);
    const tabulatorRef = useRef<any>(null);

    useEffect(() => {
        if (tableRef.current && !tabulatorRef.current) {
            tabulatorRef.current = new Tabulator(tableRef.current, {
                data: corporationsTableData, 
                layout: "fitColumns",
                responsiveLayout: "hide",
                columns: [
                    { title: "Corporação", field: "CorporationName", headerFilter: "input", headerFilterPlaceholder: "Buscar corporação..." },
                    {
                        title: "Organizações",
                        field: "Organizations",
                        formatter: (cell: any) => {
                            const orgNames = cell.getValue().map((org: Organization) => org.OrganizationName).join(', ');
                            return orgNames || 'N/A';
                        }, 
                    },
                    {
                        title: "Ações",
                        formatter: () => "<button class='btn-editar'>Editar</button>",
                        width: 120,
                        hozAlign: "center",
                        cellClick: (e: any, cell: any) => {
                            const rowData = cell.getRow().getData();
                            handleEditCorporation(rowData); 
                        },
                        headerSort: false 
                    }
                ],
            });
        }
    }, []);

    useEffect(() => {
        if (tabulatorRef.current) {
            tabulatorRef.current.replaceData(corporationsTableData);
        }
    }, [corporationsTableData]);

    return (
        <div className="card mb-5 mb-xl-10">
            <div className="card-header d-flex justify-content-between align-items-center mt-4">
                <Typography variant="h4" component="h2" className="fw-bolder m-0">
                    Corporações
                </Typography>
            </div>
            <div className="card-body">
                <div ref={tableRef}></div>
            </div>
        </div>
    );
};

export default Corporacoes;